import React, { useEffect, useRef } from 'react';
import L from 'leaflet';

export interface VehicleMarkerData {
    vehicleName: string;
    lon: number;
    lat: number;
    heading?: number;
    status: string;
    battery?: number;
}

interface VehicleMarkerProps {
    map: ReturnType<typeof L.map> | null;
    vehicle: VehicleMarkerData;
    selected?: boolean;
    onClick?: (vehicleName: string) => void;
}

// 根据状态获取颜色
const getStatusColor = (status: string): string => {
    switch (status) {
        case 'RUNNING':
            return '#1890ff';
        case 'ONLINE':
        case 'IDLE':
            return '#52c41a';
        case 'CHARGING':
            return '#faad14';
        case 'ERROR':
            return '#ff4d4f';
        case 'OFFLINE':
        default:
            return '#999999';
    }
};

// 创建车辆图标
const createVehicleIcon = (
    status: string,
    heading: number = 0,
    selected: boolean = false
): ReturnType<typeof L.divIcon> => {
    const color = getStatusColor(status);
    const size = selected ? 36 : 28;
    const borderWidth = selected ? 3 : 2;

    return (L as any).divIcon({
        className: 'vehicle-marker',
        html: `
            <div style="
                width: ${size}px;
                height: ${size}px;
                border-radius: 50%;
                background-color: ${color};
                border: ${borderWidth}px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                transform: rotate(${heading}deg);
                transition: transform 0.3s ease;
            ">
                <svg viewBox="0 0 24 24" width="${size * 0.6}" height="${size * 0.6}" fill="white">
                    <path d="M12 2L4 14h8v8l8-12h-8V2z"/>
                </svg>
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

/**
 * 车辆标记组件
 * 在地图上显示车辆位置和状态
 */
const VehicleMarker: React.FC<VehicleMarkerProps> = ({
    map,
    vehicle,
    selected = false,
    onClick,
}) => {
    const markerRef = useRef<ReturnType<typeof L.marker> | null>(null);

    useEffect(() => {
        if (!map) return;

        const { vehicleName, lon, lat, heading = 0, status, battery } = vehicle;

        // 创建或更新标记
        if (!markerRef.current) {
            const marker = (L as any).marker([lat, lon], {
                icon: createVehicleIcon(status, heading, selected),
            });

            marker.bindPopup(`
                <div style="min-width: 120px;">
                    <div style="font-weight: bold; margin-bottom: 4px;">${vehicleName}</div>
                    <div>状态: ${status}</div>
                    ${battery !== undefined ? `<div>电量: ${battery}%</div>` : ''}
                </div>
            `);

            marker.on('click', () => {
                onClick?.(vehicleName);
            });

            marker.addTo(map);
            markerRef.current = marker;
        } else {
            // 更新位置
            markerRef.current.setLatLng([lat, lon]);
            // 更新图标
            markerRef.current.setIcon(createVehicleIcon(status, heading, selected));
            // 更新弹窗内容
            markerRef.current.setPopupContent(`
                <div style="min-width: 120px;">
                    <div style="font-weight: bold; margin-bottom: 4px;">${vehicleName}</div>
                    <div>状态: ${status}</div>
                    ${battery !== undefined ? `<div>电量: ${battery}%</div>` : ''}
                </div>
            `);
        }

        return () => {
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }
        };
    }, [map, vehicle, selected, onClick]);

    return null;
};

export default React.memo(VehicleMarker);
